import type { TaxCodesQueryParams, TaxCodesResponse } from './types';

import {
  keepPreviousData,
  type QueryFunctionContext,
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import type { VestigasHTTPError } from '@vestigas/data';

import { queryKeysTaxCode } from '../queryKeys';

import { fetchTaxCodes } from './fetchTaxCodes';
import { zodTaxCodesQueryParamsSchema } from './validations';

/**
 * Generates reusable options for the paginated tax-code list query.
 *
 * @param queryParams - Filters and pagination for the tax-code list.
 * @param options - Additional query options.
 * @returns Canonical query options for fetching tax codes.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const getTaxCodesQueryOptions = <TData = TaxCodesResponse>({
  queryParams,
  options,
}: {
  queryParams: Partial<TaxCodesQueryParams>;
  options?: Omit<
    UseQueryOptions<TaxCodesResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) => {
  const canonicalQueryParams = zodTaxCodesQueryParamsSchema.parse(queryParams);

  return queryOptions({
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchTaxCodes(canonicalQueryParams, { signal }),
    queryKey: queryKeysTaxCode.getAll(canonicalQueryParams),
    staleTime: ms('60s'),
    throwOnError: false,
    ...options,
  });
};

/**
 * Fetches a paginated tax-code list while retaining the previous page during
 * page transitions.
 *
 * @param queryParams - Filters and pagination for the tax-code list.
 * @param options - Additional query options.
 * @returns The tax-code query result.
 * @see https://app.dev.vestigas.com/redoc#tag/Tax-Codes
 */
export const useQueryTaxCodes = <TData = TaxCodesResponse>(
  queryParams: Partial<TaxCodesQueryParams>,
  options?: Omit<
    UseQueryOptions<TaxCodesResponse, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getTaxCodesQueryOptions<TData>({ options, queryParams }));
